import { useEffect, useState, useSyncExternalStore } from 'react';

export type ConnectivityStatus = 'online' | 'offline' | 'reconnected';

// Only `false` is trustworthy: the browser reports `true` whenever a network interface exists, so a
// captive portal or a dead uplink still reads as online. Treat this as "definitely offline" only.
const subscribe = (onChange: () => void) => {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
};

const getSnapshot = () => window.navigator.onLine;
const getServerSnapshot = () => true;

export const useOnline = (): boolean =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

// Hold before announcing a drop, so a blip between two requests doesn't flash a banner.
const OFFLINE_GRACE_MS = 2_000;
// How long "Back online" stays up before the banner leaves entirely.
const RECONNECTED_MS = 3_000;

// Drives the connectivity banner: 'offline' after a sustained drop, 'reconnected' briefly on
// recovery, then back to 'online'. Recovery is announced only when the drop was actually shown —
// a blip that never surfaced shouldn't produce a "Back online" the user can't explain.
export const useConnectivityStatus = (): ConnectivityStatus => {
  const online = useOnline();
  const [status, setStatus] = useState<ConnectivityStatus>('online');

  useEffect(() => {
    if (!online) {
      // Drop back out of the success state at once, or "Back online" stays on screen while the
      // user is offline. Clearing it also cancels the pending recovery timer.
      setStatus((prev) => (prev === 'reconnected' ? 'online' : prev));
      const timer = setTimeout(() => setStatus('offline'), OFFLINE_GRACE_MS);
      return () => clearTimeout(timer);
    }
    setStatus((prev) => (prev === 'offline' ? 'reconnected' : 'online'));
    return undefined;
  }, [online]);

  useEffect(() => {
    if (status !== 'reconnected') return undefined;
    const timer = setTimeout(() => setStatus('online'), RECONNECTED_MS);
    return () => clearTimeout(timer);
  }, [status]);

  return status;
};
